import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ListAgentProposalsResult,
} from '@whinerp/client';
import { Button, EmptyState, ErrorState, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatRelativeDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-27 Agent proposal queue.
 *
 * Three controls per card, never two. Accept-or-reject throws away the most
 * useful thing a reviewer produces: an edited-then-accepted proposal says the
 * agent was nearly right AND names the correction, which is the only signal
 * that improves it. A queue with two buttons collects approvals and silence.
 *
 * A dismissal asks why for the same reason. "No" teaches nothing; "no, this
 * customer asked us not to call" teaches the next version.
 *
 * And a failed accept keeps the proposal in the queue with the reason. The
 * work of reviewing it has already been done; losing it to a validation error
 * makes somebody do it twice.
 */

type Proposals = ListAgentProposalsResult;
type Proposal = Proposals['proposals'][number];

function ProposalQueueScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [draftArguments, setDraftArguments] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const [failure, setFailure] = useState<{ proposalId: string; reason: string } | null>(null);

  const queue = useQuery<Proposals, ApiError>({
    queryKey: ['agent-proposals'],
    queryFn: () => api.listAgentProposals({}),
  });

  const decide = useMutation({
    mutationFn: (input: {
      proposalId: string;
      decision: 'accept' | 'edit' | 'dismiss';
      editedArguments?: Record<string, string> | null;
      note?: string | null;
    }) =>
      api.decideAgentProposal({
        path: { proposalId: input.proposalId },
        body: {
          decision: input.decision,
          ...(input.editedArguments ? { editedArguments: input.editedArguments } : {}),
          ...(input.note ? { note: input.note } : {}),
        },
      }),
    onSuccess: () => {
      setEditing(null);
      setDismissing(null);
      setNote('');
      setFailure(null);
      void queryClient.invalidateQueries({ queryKey: ['agent-proposals'] });
    },
    onError: (error: unknown, variables) => {
      /*
       * The proposal stays in the queue.
       *
       * The work of reading the reasoning and checking the evidence has
       * already been done. Losing the card to a validation error makes
       * somebody do it twice, and the second time they read it less carefully.
       */
      setFailure({
        proposalId: variables.proposalId,
        reason: error instanceof Error ? error.message : t('error.unexpected'),
      });
    },
  });

  if (queue.isPending) return <ProposalsLoading />;
  if (queue.isError) return <ProposalsError error={queue.error} retry={() => void queue.refetch()} />;
  if (queue.data.proposals.length === 0) return <ProposalsEmpty reason="no-records" />;

  const startEditing = (proposal: Proposal): void => {
    setEditing(proposal.proposalId);
    setDraftArguments({ ...proposal.arguments });
    setDismissing(null);
  };

  return (
    <div data-component="ProposalQueue" className="mx-auto flex w-full max-w-reading flex-col gap-3">
      <SectionLabel>{t('proposal.title')}</SectionLabel>

      {queue.data.proposals.map((proposal) => (
        <Panel key={proposal.proposalId}>
          <article data-proposal={proposal.proposalId} className="flex flex-col gap-2">
            <header className="flex flex-wrap items-baseline gap-2">
              <span className="text-lead text-ink">{proposal.summary}</span>
              <span className="text-meta text-ink-meta">
                {proposal.agentName} · {formatRelativeDate(proposal.createdAt, format)}
              </span>
            </header>

            {/* Why this one deserves a harder look, above the reasoning
                rather than below it: a reviewer who has already read a
                plausible explanation is reading the warning for confirmation
                instead of reading it at all. */}
            {proposal.caution ? (
              <p
                role="alert"
                data-proposal-caution={proposal.proposalId}
                className="rounded-card bg-warning-tint px-3 py-2 text-dense text-warning-text"
              >
                {proposal.caution}
              </p>
            ) : null}

            {/* The reasoning, always. A proposal nobody can interrogate is
                rubber-stamped or ignored, and both are worse than none. */}
            <div className="flex flex-col gap-0.5">
              <SectionLabel>{t('proposal.why')}</SectionLabel>
              <p data-proposal-reasoning={proposal.proposalId} className="text-body text-ink">
                {proposal.reasoning}
              </p>
            </div>

            {proposal.evidence.length > 0 ? (
              <div className="flex flex-col gap-0.5">
                <SectionLabel>{t('proposal.evidence')}</SectionLabel>
                <ul data-proposal-evidence={proposal.proposalId} className="flex flex-col gap-0.5">
                  {proposal.evidence.map((line) => (
                    <li key={line} className="text-dense text-ink-meta">
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {failure?.proposalId === proposal.proposalId ? (
              <p
                role="alert"
                data-proposal-failed={proposal.proposalId}
                className="rounded-card bg-warning-tint px-3 py-2 text-dense text-warning-text"
              >
                {t('proposal.failed', { values: { reason: failure.reason } })} {t('proposal.stillHere')}
              </p>
            ) : null}

            {editing === proposal.proposalId ? (
              <div data-editing={proposal.proposalId} className="flex flex-col gap-2">
                {Object.entries(draftArguments).map(([key, value]) => (
                  <label key={key} className="flex flex-col gap-1 text-dense text-ink">
                    {key}
                    <input
                      data-argument={key}
                      aria-label={key}
                      value={value}
                      onChange={(event) =>
                        setDraftArguments((current) => ({ ...current, [key]: event.target.value }))
                      }
                      className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
                    />
                  </label>
                ))}
                <span>
                  <Button
                    intent="primary"
                    data-save-edit={proposal.proposalId}
                    busy={decide.isPending}
                    onClick={() =>
                      decide.mutate({
                        proposalId: proposal.proposalId,
                        decision: 'edit',
                        editedArguments: draftArguments,
                      })
                    }
                  >
                    {t('proposal.save')}
                  </Button>
                </span>
              </div>
            ) : dismissing === proposal.proposalId ? (
              <div data-dismissing={proposal.proposalId} className="flex flex-col gap-1">
                <label className="flex flex-col gap-1 text-dense text-ink">
                  {t('proposal.dismissReason')}
                  <textarea
                    data-dismiss-note
                    aria-label={t('proposal.dismissReason')}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    className="rounded-control border border-rule bg-surface px-2 py-1 text-dense text-ink"
                  />
                </label>
                {/* Said at the point of asking, not afterwards. */}
                <span className="text-meta text-ink-meta">{t('proposal.dismissHelp')}</span>
                <span>
                  <Button
                    intent="secondary"
                    data-confirm-dismiss={proposal.proposalId}
                    busy={decide.isPending}
                    onClick={() =>
                      decide.mutate({ proposalId: proposal.proposalId, decision: 'dismiss', note })
                    }
                  >
                    {t('proposal.dismiss')}
                  </Button>
                </span>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {/* Three, always. Two buttons collect approvals and silence. */}
                <Button
                  intent="primary"
                  icon="Check"
                  data-accept={proposal.proposalId}
                  busy={decide.isPending}
                  onClick={() => decide.mutate({ proposalId: proposal.proposalId, decision: 'accept' })}
                >
                  {t('proposal.accept')}
                </Button>
                <Button
                  intent="secondary"
                  icon="PencilSimple"
                  data-edit={proposal.proposalId}
                  onClick={() => startEditing(proposal)}
                >
                  {t('proposal.edit')}
                </Button>
                <Button
                  intent="ghost"
                  icon="X"
                  data-dismiss={proposal.proposalId}
                  onClick={() => {
                    setDismissing(proposal.proposalId);
                    setEditing(null);
                    setNote('');
                  }}
                >
                  {t('proposal.dismiss')}
                </Button>
              </div>
            )}
          </article>
        </Panel>
      ))}
    </div>
  );
}

function ProposalsEmpty({
  reason,
}: {
  reason: 'no-records' | 'filtered-to-nothing' | 'permission-denied';
}): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason={reason}
      illustration="Sparkle"
      title={t('proposal.empty.title')}
      body={`${t('proposal.empty.body')} ${t('proposal.settings')}`}
      primary={{ label: t('empty.action.openAgents'), icon: 'Sparkle', href: '/agents/activity' }}
    />
  );
}

function ProposalsLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="mx-auto flex w-full max-w-reading flex-col gap-3" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      {Array.from({ length: 3 }, (_, card) => (
        <Skeleton key={card} className="h-plot w-full" />
      ))}
    </div>
  );
}

function ProposalsError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="The proposal queue could not be loaded"
      body={error instanceof Error ? error.message : 'The queue did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-27',
  route: '/agents/proposals',
  surface: 'page',
  title: 'Waiting for you',
  features: ['F-111'],
  entitlement: 'F-111',
  reachedBy: [{ kind: 'nav', item: 'agents' }],
  /*
   * No shared component is claimed.
   *
   * EmptyState renders only when the queue is empty, and the queue in a seeded
   * workspace is not - so declaring it here made the render check look for
   * something that correctly was not there. The empty state is declared in
   * `states` below, which the check renders on its own.
   */
  components: [],
  elements: ['ProposalQueue'],
  states: { empty: ProposalsEmpty, loading: ProposalsLoading, error: ProposalsError },
  component: ProposalQueueScreen,
});

export default screen;
